import React, { useState, useEffect } from 'react';
import { Box, Button } from '@mui/material';
import { ArrowBack, History } from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';

import { apiService } from '../services/api';
import { LoadingSpinner } from './LoadingSpinner';
import { MonitoredLegislationsTable } from './MonitoredLegislationsTable';
import {
  AgencyMonitoringBrowsePanel,
  MonitoringSelectionDialogShell,
  type AgencyRow,
} from './monitoringSelection';

interface AgencyMonitoringDialogProps {
  open: boolean;
  onClose: () => void;
  onOpenGlobalHistory: () => void;
  onOpenAgencyLogs: (agency: AgencyRow) => void;
}

export function AgencyMonitoringDialog({
  open,
  onClose,
  onOpenGlobalHistory,
  onOpenAgencyLogs,
}: AgencyMonitoringDialogProps) {
  const queryClient = useQueryClient();
  const [localAgencies, setLocalAgencies] = useState<AgencyRow[]>([]);
  const [showMonitoredLegislations, setShowMonitoredLegislations] = useState(false);
  const [selectedAgencyForLegislations, setSelectedAgencyForLegislations] = useState<AgencyRow | null>(null);

  const { data: agenciesData, isLoading } = useQuery(
    ['agencies-monitoring'],
    () => apiService.getAgenciesMonitoring(),
    { enabled: open, refetchOnWindowFocus: false },
  );

  const toggleAgencyMutation = useMutation(
    ({ agencyCode, agencyName }: { agencyCode: string; agencyName: string }) =>
      apiService.toggleAgencyMonitoring(agencyCode, agencyName),
    {
      onSuccess: (data, { agencyCode }) => {
        setLocalAgencies((prev) =>
          prev.map((agency) =>
            agency.code === agencyCode
              ? { ...agency, isMonitored: data.is_monitored, monitoredSince: data.monitored_since }
              : agency,
          ),
        );
        toast.success(data.message);
        queryClient.invalidateQueries(['agencies-monitoring']);
        queryClient.invalidateQueries(['agencies-monitoring-history']);
        queryClient.invalidateQueries(['agencies-monitoring-delivery-logs']);
        queryClient.invalidateQueries(['agency-delivery-logs']);
        queryClient.invalidateQueries(['agency-history-detail']);
        queryClient.invalidateQueries(['legislation']);
      },
      onError: (error: unknown) => {
        const message = error instanceof Error ? error.message : 'Erro ao atualizar monitoramento da agência';
        toast.error(message);
      },
    },
  );

  useEffect(() => {
    if (agenciesData?.agencies) {
      setLocalAgencies(agenciesData.agencies);
    }
  }, [agenciesData]);

  const handleToggle = (agencyCode: string, agencyName: string) => {
    toggleAgencyMutation.mutate({ agencyCode, agencyName });
  };

  const handleViewMonitoredLegislations = (agency: AgencyRow) => {
    setSelectedAgencyForLegislations(agency);
    setShowMonitoredLegislations(true);
  };

  const handleBackToAgencies = () => {
    setShowMonitoredLegislations(false);
    setSelectedAgencyForLegislations(null);
  };

  return (
    <MonitoringSelectionDialogShell
      open={open}
      onClose={onClose}
      title="Monitorar agências"
      subtitle="Ative/desative o monitoramento completo por agência (padrão PA)."
      actions={
        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 1 }}>
          <Button onClick={onOpenGlobalHistory} variant="outlined" startIcon={<History />}>
            Histórico
          </Button>
          <Box sx={{ flexGrow: 1 }} />
          <Button onClick={onClose} variant="contained">
            Fechar
          </Button>
        </Box>
      }
    >
      {showMonitoredLegislations && selectedAgencyForLegislations ? (
        <>
          <Box display="flex" alignItems="center" gap={2} mb={3}>
            <Button onClick={handleBackToAgencies} variant="outlined" size="small" startIcon={<ArrowBack />}>
              Voltar
            </Button>
          </Box>
          <MonitoredLegislationsTable
            agencyCode={selectedAgencyForLegislations.code}
            agencyName={selectedAgencyForLegislations.name}
          />
        </>
      ) : isLoading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
          <LoadingSpinner message="Carregando agências..." />
        </Box>
      ) : (
        <AgencyMonitoringBrowsePanel
          agencies={localAgencies}
          mode="monitoring"
          onToggleAgency={handleToggle}
          isToggleLoading={toggleAgencyMutation.isLoading}
          onViewHistory={onOpenAgencyLogs}
          onViewLegislations={handleViewMonitoredLegislations}
        />
      )}
    </MonitoringSelectionDialogShell>
  );
}
